import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SingleSelectCards, StepNavigation } from '../../components/quiz';
import { AuthModal } from '../../components/shared';
import { Button } from '../../components/ui';
import { track, page, EVENTS } from '../../utils/analytics';
import { buildRentalSignupUrl } from '../../utils/signupUrl';
import { useApp } from '../../contexts/AppContext';
import type { SelectableCardOption } from '../../types';

// ── Types ─────────────────────────────────────────────────────────────────────

type Phase = 'quiz' | 'interstitial' | 'loading' | 'results';

interface Answers {
  q1?: string;
  q2?: string;
  q3?: string;
  q4?: string;
  q5?: string;
  q6?: string;
  q7?: string;
  q8?: string;
  q9?: string;
  q10?: string;
  q11?: string;
  q12?: string;
  q13?: string;
}

// ── Score map ──────────────────────────────────────────────────────────────────
// q2 has no score (landlord type is segmentation only)

const SCORES: Record<string, Record<string, number>> = {
  q1: { ONE: 1, TWO_THREE: 2, FOUR_TEN: 3, TEN_PLUS: 4 },
  q3: { OPTIMIZE: 1, PAYDOWN: 1, SCALE: 2, PASSIVE: 3 },
  q4: { FREE_CLEAR: 1, PART_MORT: 2, ALL_MORT: 3, HELOC: 4 },
  q5: { UNDER_1H: 1, ONE_THREE_H: 2, THREE_SIX_H: 3, SIX_PLUS: 4 },
  q6: { PARTIAL_AUTO: 1, SPREADSHEET: 2, MANUAL: 3, POOL: 4 },
  q7: { PF_USER: 1, PF_AWARE: 2, PF_OPEN: 3, PF_NONE: 4 },
  q8: { RES_STRONG: 1, RES_CASUAL: 2, RES_CREDIT: 3, RES_NONE: 4 },
  q9: { VAC_NONE: 1, VAC_SHORT: 2, VAC_MED: 3, VAC_MULTI: 4 },
  q10: { CAP_READY: 1, CAP_MIX: 2, CAP_CREDIT: 3, CAP_STRESS: 4 },
  q11: { PROFIT_CLEAR: 1, PROFIT_FUZZY: 2, PROFIT_MURKY: 3, PROFIT_UNKNOWN: 4 },
  q12: { TAX_AUTO: 1, TAX_EST: 2, TAX_REACT: 3, TAX_STRESS: 4 },
  q13: { SYS_AUTO: 1, SYS_PARTIAL: 2, SYS_MANUAL: 3, SYS_CHAOS: 4 },
};

function getScore(q: number, answerId: string): number {
  return SCORES[`q${q}`]?.[answerId] ?? 0;
}

function getTotalScore(answers: Answers): number {
  return Object.entries(answers)
    .filter(([key]) => key !== 'q2')
    .reduce((sum, [key, val]) => {
      const q = parseInt(key.replace('q', ''));
      return sum + (val ? getScore(q, val) : 0);
    }, 0);
}

// ── Question definitions ────────────────────────────────────────────────────────

const Q1_OPTIONS: SelectableCardOption[] = [
  { id: 'ONE', icon: '🌱', title: '1 property', description: 'Building my foundation' },
  { id: 'TWO_THREE', icon: '📈', title: '2-3 properties', description: 'Growing steadily' },
  { id: 'FOUR_TEN', icon: '💼', title: '4-10 properties', description: 'Running a real portfolio' },
  { id: 'TEN_PLUS', icon: '🏦', title: '10+ properties', description: 'Full-scale rental business' },
];

const Q2_OPTIONS: SelectableCardOption[] = [
  { id: 'LTR', icon: '🏡', title: "I'm a long-term rental investor", description: 'Residential, 12+ month leases' },
  { id: 'STR', icon: '🏖️', title: "I'm a short-term rental host", description: 'Airbnb, VRBO, or similar' },
  { id: 'MIX', icon: '🔀', title: 'I run a mix of both', description: 'Short and long-term properties' },
  { id: 'COM', icon: '🏢', title: 'I manage commercial or multi-unit properties', description: '' },
];

const Q3_OPTIONS: SelectableCardOption[] = [
  { id: 'OPTIMIZE', icon: '⚙️', title: "I'm optimizing", description: 'Squeezing more profit from what I already have' },
  { id: 'PAYDOWN', icon: '🔥', title: "I'm aggressively paying down debt", description: 'To own free and clear' },
  { id: 'SCALE', icon: '📊', title: "I'm scaling", description: 'Using cash flow to acquire more properties' },
  { id: 'PASSIVE', icon: '🎯', title: "I'm building passive income", description: 'To eventually replace my day job' },
];

const Q4_OPTIONS: SelectableCardOption[] = [
  { id: 'FREE_CLEAR', icon: '✅', title: 'All properties are paid off', description: 'I own them free and clear' },
  { id: 'PART_MORT', icon: '🔀', title: 'Some paid off, some still have mortgages', description: '' },
  { id: 'ALL_MORT', icon: '📋', title: 'All of my properties still carry a mortgage', description: '' },
  { id: 'HELOC', icon: '🏦', title: 'I have a HELOC or private/hard money financing', description: 'On some properties' },
];

const Q5_OPTIONS: SelectableCardOption[] = [
  { id: 'UNDER_1H', icon: '⚡', title: 'Under 1 hour', description: 'Almost fully automated' },
  { id: 'ONE_THREE_H', icon: '🕐', title: '1-3 hours', description: 'Manageable, but not ideal' },
  { id: 'THREE_SIX_H', icon: '🕒', title: '3-6 hours', description: 'It takes real time every week' },
  { id: 'SIX_PLUS', icon: '😩', title: '6+ hours', description: "It's basically a part-time job" },
];

const Q6_OPTIONS: SelectableCardOption[] = [
  { id: 'PARTIAL_AUTO', icon: '🤖', title: 'I have automation in place', description: 'Money moves itself to the right buckets' },
  { id: 'SPREADSHEET', icon: '📊', title: 'I follow a spreadsheet or mental system', description: 'I built it myself' },
  { id: 'MANUAL', icon: '🔄', title: 'I manually move money each month', description: 'To different accounts' },
  { id: 'POOL', icon: '🗂️', title: 'It all goes into one account', description: 'I pay bills from there and keep the rest' },
];

const Q7_OPTIONS: SelectableCardOption[] = [
  { id: 'PF_USER', icon: '🏆', title: "Yes - I actively use it", description: 'For my rental income' },
  { id: 'PF_AWARE', icon: '📖', title: "I've heard of it", description: "But haven't set it up yet" },
  { id: 'PF_OPEN', icon: '💡', title: "No - but I'd love a system", description: 'That automatically allocates money when rent hits' },
  { id: 'PF_NONE', icon: '🤷', title: 'I pay bills first', description: "And keep whatever's left" },
];

const Q8_OPTIONS: SelectableCardOption[] = [
  { id: 'RES_STRONG', icon: '🏦', title: 'I have a dedicated reserve fund', description: 'Gets topped up automatically every month' },
  { id: 'RES_CASUAL', icon: '🧮', title: 'I estimate in my head', description: 'It usually works out' },
  { id: 'RES_CREDIT', icon: '💳', title: 'Repairs go on a credit card', description: 'I pay it down afterward' },
  { id: 'RES_NONE', icon: '😰', title: "I don't have a system", description: 'It stresses me out every time something breaks' },
];

// Q9 has two variants based on landlord type
const Q9_OPTIONS_STANDARD: SelectableCardOption[] = [
  { id: 'VAC_NONE', icon: '✅', title: 'Never / over 2 years ago', description: "I've had strong continuity" },
  { id: 'VAC_SHORT', icon: '📅', title: 'Within the past year - a short gap', description: 'A few weeks' },
  { id: 'VAC_MED', icon: '🛑', title: 'Within the past year - a significant vacancy', description: '1-3 months' },
  { id: 'VAC_MULTI', icon: '🔥', title: 'Multiple times in the past year', description: 'A recurring challenge' },
];

const Q9_OPTIONS_STR: SelectableCardOption[] = [
  { id: 'VAC_NONE', icon: '✅', title: 'Never - I stay fully booked year-round', description: '' },
  { id: 'VAC_SHORT', icon: '📅', title: 'Last season had a slow patch', description: 'A few weeks' },
  { id: 'VAC_MED', icon: '🛑', title: 'Last season had a 1-2 month income slump', description: '' },
  { id: 'VAC_MULTI', icon: '🔥', title: 'I have major slow seasons', description: 'That consistently hurt cash flow' },
];

// Q10 has two variants based on FREE_CLEAR status
const Q10_OPTIONS_STANDARD: SelectableCardOption[] = [
  { id: 'CAP_READY', icon: '💪', title: 'I pull from a dedicated CapEx reserve fund', description: "I've been building it for this" },
  { id: 'CAP_MIX', icon: '😬', title: 'A mix of current cash flow and personal savings', description: '' },
  { id: 'CAP_CREDIT', icon: '💳', title: 'It goes on a credit card', description: 'And I scramble to cover the mortgage too' },
  { id: 'CAP_STRESS', icon: '😱', title: 'This scenario genuinely stresses me out', description: "I don't have a clear plan" },
];

const Q10_OPTIONS_FREE_CLEAR: SelectableCardOption[] = [
  { id: 'CAP_READY', icon: '💪', title: 'I pull from a dedicated CapEx reserve fund', description: "I've been building it for this" },
  { id: 'CAP_MIX', icon: '😬', title: 'A mix of current cash flow and personal savings', description: '' },
  { id: 'CAP_CREDIT', icon: '💳', title: 'It goes on a credit card', description: 'I pay it down over a few months' },
  { id: 'CAP_STRESS', icon: '😱', title: 'This scenario genuinely stresses me out', description: "I don't have a clear plan" },
];

const Q11_OPTIONS: SelectableCardOption[] = [
  { id: 'PROFIT_CLEAR', icon: '📊', title: 'Yes - I track it to the dollar', description: 'Every month' },
  { id: 'PROFIT_FUZZY', icon: '🧮', title: 'Roughly - I have a general sense', description: "But I don't calculate it precisely" },
  { id: 'PROFIT_MURKY', icon: '🤷', title: "Not really - I know I'm making something", description: 'But the number is murky' },
  { id: 'PROFIT_UNKNOWN', icon: '😳', title: "Honestly, I'm not sure some are even profitable", description: 'After everything' },
];

const Q12_OPTIONS: SelectableCardOption[] = [
  { id: 'TAX_AUTO', icon: '🤖', title: 'I automatically set aside a percentage', description: 'Every time rent hits' },
  { id: 'TAX_EST', icon: '🤞', title: 'I estimate at year-end', description: 'And put money aside when I can' },
  { id: 'TAX_REACT', icon: '😅', title: 'My accountant tells me what I owe', description: 'Then I scramble to find the cash' },
  { id: 'TAX_STRESS', icon: '😳', title: 'Tax season genuinely worries me', description: "I'm never sure I've saved enough" },
];

const Q13_OPTIONS: SelectableCardOption[] = [
  { id: 'SYS_AUTO', icon: '🤖', title: 'Fully automated', description: 'Money moves itself - I just check in occasionally' },
  { id: 'SYS_PARTIAL', icon: '🔧', title: 'Partially automated', description: 'But I still have to manually intervene regularly' },
  { id: 'SYS_MANUAL', icon: '📋', title: 'Mostly manual', description: 'My system completely depends on me showing up' },
  { id: 'SYS_CHAOS', icon: '🌀', title: 'Organized chaos', description: "I'm mostly reacting when things come up" },
];

// ── Interstitial types ─────────────────────────────────────────────────────────

interface Interstitial {
  narrator: string | null;
  spId: string | null;
  variant: string;
}

// ── Social proof library ────────────────────────────────────────────────────────

interface SocialProofSnippet {
  id: string;
  quote: string;
  author: string;
  role: string;
}

// SP2 and SP3 retired - those triggers now use narrator-only interstitials
const SOCIAL_PROOF: Record<string, SocialProofSnippet> = {
  SP1: {
    id: 'SP1',
    quote: "I used to spend Sunday evenings manually moving rent money between accounts. Now Sequence handles it automatically the second rent hits - I haven't had to touch it in months.",
    author: 'Jackson M.',
    role: '4 long-term rental properties',
  },
  SP4: {
    id: 'SP4',
    quote: "June and July are incredible - but November used to be brutal for my beach house. Sequence automatically builds my off-season reserve during the high months. I don't even think about it anymore.",
    author: 'Stephanie P.',
    role: 'Short-term rental host, 2 properties',
  },
  SP5: {
    id: 'SP5',
    quote: "I had a 6-week vacancy that used to mean a stressful conversation about dipping into personal savings. Sequence had been auto-building my reserve every month - I didn't touch a single personal dollar.",
    author: 'Norman K.',
    role: 'Long-term landlord, 2 properties',
  },
  SP6: {
    id: 'SP6',
    quote: "Every repair used to go on my Amex. Now Sequence drips a fixed amount into a repair pod every time rent deposits - by the time anything breaks, the money's already sitting there waiting.",
    author: 'Jake S.',
    role: '2-property landlord',
  },
  SP7: {
    id: 'SP7',
    quote: "I set one rule: every time rent hits, 25% goes straight to principal. My payoff date moved up by 4 years. I didn't do anything - Sequence just did it automatically every single month.",
    author: 'Timothy H.',
    role: '3-property investor, all mortgaged',
  },
  SP8: {
    id: 'SP8',
    quote: "Tax season used to be a nightmare - I'd owe $18,000 and scramble to find it. Now Sequence takes 28% of every rent deposit and parks it in a tax pod automatically. The check just appears.",
    author: 'Marlon M.',
    role: 'Commercial property owner',
  },
};

// ── Interstitial logic ─────────────────────────────────────────────────────────
// Rules:
// - Narrator-only: pure insight, no quote. Most interstitials.
// - Narrator + SP: narrator line above quote. Max 1 SP shown per user (spShown flag).
// - If SP already shown, SP slot becomes null but narrator still fires.

function getInterstitialAfterQ(q: number, answers: Answers, spShown: boolean): Interstitial | null {
  // Q4: narrator only, FREE_CLEAR landlords
  if (q === 4 && answers.q4 === 'FREE_CLEAR') {
    return { narrator: "Debt-free puts you ahead of most landlords. The leaks from here look different - they're usually in how the cash flows after it lands, not in the mortgage.", spId: null, variant: 'debt_free' };
  }

  // Q5: narrator always - universal reframe
  if (q === 5) {
    return { narrator: "Rental finances should take under an hour a month. If it's more than that, you're executing the same decisions manually every single month.", spId: null, variant: 'time_investment' };
  }

  // Q6: narrator for all, SP1 added only for MANUAL/POOL (highest pain, first SP slot)
  if (q === 6) {
    if (answers.q6 === 'SPREADSHEET') {
      return { narrator: "A spreadsheet only runs when you open it. Your money shouldn't depend on that.", spId: null, variant: 'spreadsheet' };
    }
    if (answers.q6 === 'PARTIAL_AUTO') {
      return { narrator: "Partial automation means you're still the backup. The gaps you haven't closed are usually where the money goes.", spId: null, variant: 'partial_automation' };
    }
    if (answers.q6 === 'MANUAL' || answers.q6 === 'POOL') {
      return { narrator: "The most common setup - and the one with the most fixable leaks.", spId: spShown ? null : 'SP1', variant: 'manual_transfers' };
    }
  }

  // Q7: narrator for PF_AWARE or PF_OPEN
  if (q === 7 && (answers.q7 === 'PF_AWARE' || answers.q7 === 'PF_OPEN')) {
    return { narrator: "Rent is predictable income - which makes it perfect for Profit First. The landlords who actually run it didn't find more discipline. They automated it.", spId: null, variant: 'profit_first' };
  }

  // Q8: narrator + SP6 if high pain
  if (q === 8 && (answers.q8 === 'RES_CREDIT' || answers.q8 === 'RES_NONE')) {
    return { narrator: "Every rental will need a major repair soon. The question is whether the money is already waiting.", spId: spShown ? null : 'SP6', variant: 'repair_reserves' };
  }

  // Q9: narrator + SP4/SP5 if vacancy occurred
  if (q === 9 && answers.q9 !== 'VAC_NONE') {
    const isSTR = answers.q2 === 'STR';
    const narrator = isSTR
      ? "The hosts who sleep easy in slow season built their buffer during peak months."
      : "A vacancy doesn't have to hurt if you've been building the cushion all along.";
    return { narrator, spId: spShown ? null : (isSTR ? 'SP4' : 'SP5'), variant: isSTR ? 'vacancy_str' : 'vacancy_ltr' };
  }

  // Q10: narrator + SP7 for mortgaged landlords (the "mortgage as wealth tool" moment)
  if (q === 10 && (answers.q4 === 'ALL_MORT' || answers.q4 === 'HELOC')) {
    return {
      narrator: "A mortgage on a rental is one of the most powerful wealth-building tools you have - if rent is automatically chipping away at the principal, not just covering the payment.",
      spId: spShown ? null : 'SP7',
      variant: 'mortgage_wealth',
    };
  }

  // Q11: narrator for murky/unknown profit clarity
  if (q === 11 && (answers.q11 === 'PROFIT_MURKY' || answers.q11 === 'PROFIT_UNKNOWN')) {
    return { narrator: "Most landlords can't tell you their exact monthly profit per property. Every decision about that property - sell, refi, hold - is being made without the number that matters most.", spId: null, variant: 'profit_clarity' };
  }

  // Q12: narrator + SP8 for tax anxiety
  if (q === 12 && (answers.q12 === 'TAX_STRESS' || answers.q12 === 'TAX_REACT')) {
    return { narrator: "Tax surprises aren't a planning problem. They're a timing problem.", spId: spShown ? null : 'SP8', variant: 'tax_anxiety' };
  }

  return null;
}

// ── Profile definitions ────────────────────────────────────────────────────────

interface Profile {
  scoreMin: number;
  scoreMax: number;
  badge: string;
  color: string;
  name: string;
  headline: string;
  body: string[];
  oneliner: string;
  cta: string;
}

const PROFILES: Profile[] = [
  {
    scoreMin: 12,
    scoreMax: 20,
    badge: '🟢',
    color: 'green',
    name: 'The Optimized Operator',
    headline: 'Your Rental Portfolio is Running Like a Business.',
    body: [
      "You're already doing a lot right. You have reserves, you're thinking systematically, and your income isn't running your life.",
      "The top 5% of landlords who reach full financial autopilot have one thing in common: they stop trading time for money management entirely.",
      "Based on your answers, we've identified the last few manual steps still in your system - and Sequence can remove them so your entire rental cash flow runs on autopilot while you focus on what's next.",
    ],
    oneliner: "You've done the hard work. Sequence handles what's left.",
    cta: 'Set Up My Full Autopilot',
  },
  {
    scoreMin: 21,
    scoreMax: 31,
    badge: '🟡',
    color: 'yellow',
    name: 'The Systematizer',
    headline: "You Know What Good Looks Like - You're Just Not Quite There Yet.",
    body: [
      "You've built a real system - more than most landlords ever do. But you're still spending real hours every month manually managing money that should move itself.",
      "You know a vacancy hurts, you know taxes need to be set aside - you're just doing it by hand.",
      "Sequence was built for landlords exactly like you. One automation setup could save you 3-5 hours a month and eliminate the manual top-up transfers you're doing right now.",
    ],
    oneliner: "You built the system. Sequence makes it run without you.",
    cta: 'Get 3-5 Hours Back Every Month',
  },
  {
    scoreMin: 32,
    scoreMax: 40,
    badge: '🟠',
    color: 'orange',
    name: 'The Reactive Manager',
    headline: "Your Rental Income is Working Hard. So Are You - And That's the Problem.",
    body: [
      "You're not doing anything wrong - you're just doing everything manually. Your money is sitting in accounts waiting for you to make decisions that should be automatic.",
      "Every vacancy stings harder than it needs to. Every repair is a scramble. Every tax season is a surprise.",
      "Landlords in your situation typically have $400-$1,200/month in profit leaks they can't see: timing gaps on credit cards, under-funded reserves, missed mortgage curtailment windows. Your personalized blueprint shows exactly where those leaks are.",
    ],
    oneliner: "Your income is real. Your profits are just harder to see than they should be.",
    cta: 'Put My Profits on Autopilot',
  },
  {
    scoreMin: 41,
    scoreMax: 48,
    badge: '🔴',
    color: 'red',
    name: 'The Cash Flow Firefighter',
    headline: "Your Properties Are Profitable. Your System Isn't.",
    body: [
      "Right now, your financial system depends entirely on you. If you miss a transfer, funds commingle. If a vacancy hits, reserves aren't there. If tax season arrives, it's a scramble. If a repair hits - it's a crisis.",
      "That's not a you problem - that's a system problem. And it's fixable faster than you think.",
      "Most landlords in your profile reduce financial management time by 70% in the first 30 days.",
    ],
    oneliner: "Right now your finances need you to show up. Sequence changes that.",
    cta: 'Build a System That Runs Itself',
  },
];

function getProfile(score: number): Profile {
  return PROFILES.find((p) => score >= p.scoreMin && score <= p.scoreMax) ?? PROFILES[PROFILES.length - 1];
}

// ── Progress helpers ────────────────────────────────────────────────────────────

function getMilestoneMessage(questionNumber: number): string | null {
  if (questionNumber >= 10) return "Almost done - your blueprint is being built in real time.";
  if (questionNumber >= 7) return "You're halfway there. The results will be specific to you.";
  if (questionNumber >= 4) return "Great start - keep going, this gets interesting.";
  return null;
}

// ── Loading step definitions ───────────────────────────────────────────────────

const LOADING_MESSAGES = [
  'Analyzing your rental profile...',
  'Calculating your estimated monthly money leaks...',
  'Building your personalized automation blueprint...',
];

// ── Question renderer helpers ──────────────────────────────────────────────────

function getQuestionContent(q: number, answers: Answers): { question: string; helper?: string; options: SelectableCardOption[] } {
  const isSTR = answers.q2 === 'STR';
  const isFreeClear = answers.q4 === 'FREE_CLEAR';

  switch (q) {
    case 1:
      return {
        question: 'How many rental units do you currently own or manage?',
        helper: 'More units means more complexity - and more opportunity for automation.',
        options: Q1_OPTIONS,
      };
    case 2:
      return {
        question: 'Which best describes you as a landlord?',
        helper: 'This helps us tailor your results to your exact situation.',
        options: Q2_OPTIONS,
      };
    case 3:
      return {
        question: "What's your #1 financial mission for your rentals right now?",
        options: Q3_OPTIONS,
      };
    case 4:
      return {
        question: "What's your current debt situation across your rental properties?",
        options: Q4_OPTIONS,
      };
    case 5:
      return {
        question: 'How many hours per week do you spend managing your rental finances?',
        helper: 'Transfers, bookkeeping, account top-ups, tax tracking',
        options: Q5_OPTIONS,
      };
    case 6:
      return {
        question: 'When rental income hits your account, what actually happens to it?',
        options: Q6_OPTIONS,
      };
    case 7:
      return {
        question: "Have you heard of the 'Profit First' method for business cash flow?",
        options: Q7_OPTIONS,
      };
    case 8:
      return {
        question: 'How do you handle cash reserves for repairs, maintenance, and unexpected costs?',
        options: Q8_OPTIONS,
      };
    case 9:
      return {
        question: isSTR
          ? 'When was your last significant slow season or extended gap between bookings?'
          : 'When was the last time one of your properties sat vacant for more than a week?',
        options: isSTR ? Q9_OPTIONS_STR : Q9_OPTIONS_STANDARD,
      };
    case 10:
      return {
        question: isFreeClear
          ? 'A $7,000-$9,000 repair just hit - new HVAC, roof section, or major plumbing. How do you cover it?'
          : 'A $7,000-$9,000 repair just hit - new HVAC, roof section, or major plumbing. What actually happens?',
        options: isFreeClear ? Q10_OPTIONS_FREE_CLEAR : Q10_OPTIONS_STANDARD,
      };
    case 11:
      return {
        question: 'After mortgage, taxes, insurance, repairs, and management fees - do you know your exact monthly profit per property?',
        options: Q11_OPTIONS,
      };
    case 12:
      return {
        question: 'How do you handle setting money aside for income tax on your rental earnings?',
        options: Q12_OPTIONS,
      };
    case 13:
      return {
        question: 'Gut check - how would you honestly describe your current rental financial system?',
        options: Q13_OPTIONS,
      };
    default:
      return { question: '', options: [] };
  }
}

// ── Analytics helpers ──────────────────────────────────────────────────────────

const QUESTION_KEYS: Record<number, string> = {
  1: 'portfolio_size',
  2: 'landlord_type',
  3: 'primary_goal',
  4: 'debt_status',
  5: 'time_investment',
  6: 'financial_system',
  7: 'profit_first',
  8: 'repair_reserves',
  9: 'vacancy_exposure',
  10: 'capex_preparedness',
  11: 'profit_clarity',
  12: 'tax_management',
  13: 'system_assessment',
};

function getDevice(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function RentalQuiz() {
  const navigate = useNavigate();
  const { setFlow } = useApp();

  const [phase, setPhase] = useState<Phase>('quiz');
  const [currentQ, setCurrentQ] = useState(1);
  const [answers, setAnswers] = useState<Answers>({});
  const [currentSelection, setCurrentSelection] = useState<string | null>(null);
  const [pendingInterstitial, setPendingInterstitial] = useState<Interstitial | null>(null);
  const [spShown, setSpShown] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [capturedEmail, setCapturedEmail] = useState('');

  const stepStartTimeRef = useRef<number>(Date.now());
  const utmRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid', 'utm_landing_page'];
    const utms: Record<string, string> = {};
    utmKeys.forEach((key) => {
      const val = params.get(key);
      if (val) utms[key] = val;
    });
    if (!utms.utm_landing_page) utms.utm_landing_page = 'rental';
    utmRef.current = utms;

    setFlow('rental');
    page('Rental Quiz', { ...utms });
    track(EVENTS.RENTAL_QUIZ_SHOWN, { device: getDevice(), ...utms });
  }, []);

  // Scroll to top and reset step timer whenever the displayed content changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    stepStartTimeRef.current = Date.now();
  }, [currentQ, phase]);

  // Restore previous answer when navigating back to a question
  useEffect(() => {
    const key = `q${currentQ}` as keyof Answers;
    setCurrentSelection(answers[key] ?? null);
  }, [currentQ]);

  // Loading animation: 3 steps x 2 seconds each
  useEffect(() => {
    if (phase !== 'loading') return;

    setLoadingStep(0);

    const timers = [
      setTimeout(() => setLoadingStep(1), 1000),
      setTimeout(() => setLoadingStep(2), 2000),
      setTimeout(() => setPhase('results'), 3000),
    ];

    return () => timers.forEach(clearTimeout);
  }, [phase]);

  function handleContinue() {
    if (!currentSelection) return;

    const key = `q${currentQ}` as keyof Answers;
    const newAnswers: Answers = { ...answers, [key]: currentSelection };
    setAnswers(newAnswers);

    // Find the human-readable label for the selected answer
    const { options } = getQuestionContent(currentQ, newAnswers);
    const selectedOption = options.find((o) => o.id === currentSelection);
    const timeOnQuestionSec = Math.round((Date.now() - stepStartTimeRef.current) / 1000);
    const device = getDevice();

    // Per-question event
    track(EVENTS.RENTAL_QUESTION_ANSWERED, {
      questionNumber: currentQ,
      questionKey: QUESTION_KEYS[currentQ],
      answerId: currentSelection,
      answerLabel: selectedOption?.title ?? currentSelection,
      score: getScore(currentQ, currentSelection),
      device,
      timeOnQuestionSec,
    });

    if (currentQ === 1) {
      track(EVENTS.RENTAL_QUIZ_STARTED);
    }

    if (currentQ === 13) {
      const score = getTotalScore(newAnswers);
      track(EVENTS.RENTAL_QUIZ_COMPLETED, { score, landlordType: newAnswers.q2, device });
      setShowAuthModal(true);
      return;
    }

    const interstitial = getInterstitialAfterQ(currentQ, newAnswers, spShown);
    if (interstitial) {
      if (interstitial.spId) setSpShown(true);
      setPendingInterstitial(interstitial);
      setPhase('interstitial');
      track(EVENTS.RENTAL_INTERSTITIAL_SHOWN, {
        afterQuestion: currentQ,
        afterQuestionKey: QUESTION_KEYS[currentQ],
        variant: interstitial.variant,
        type: interstitial.spId ? 'narrator_sp' : 'narrator_only',
        triggerAnswer: currentSelection,
        spId: interstitial.spId ?? null,
        device,
      });
    } else {
      setCurrentQ((prev) => prev + 1);
      setCurrentSelection(null);
    }
  }

  function handleInterstitialContinue() {
    setPendingInterstitial(null);
    setPhase('quiz');
    setCurrentQ((prev) => prev + 1);
    setCurrentSelection(null);
  }

  function handleBack() {
    if (phase === 'interstitial') {
      setPendingInterstitial(null);
      setPhase('quiz');
      return;
    }
    if (currentQ === 1) {
      navigate('/quiz');
      return;
    }
    setCurrentQ((prev) => prev - 1);
    setCurrentSelection(null);
  }

  // ── Computed values ──────────────────────────────────────────────────────────

  const progressPct = phase === 'loading' || phase === 'results'
    ? 100
    : Math.round((currentQ - 1) / 13 * 100);

  const milestoneMessage = phase === 'quiz' ? getMilestoneMessage(currentQ) : null;

  // ── Render: loading ──────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <div className="loading-page">
        <div className="loading-page__container">
          <div className="loading-page__header">
            <h1 className="loading-page__title">Calculating Your Score</h1>
            <p className="loading-page__subtitle">Tailoring every insight to your rental situation</p>
          </div>

          <div className="loading-steps">
            {LOADING_MESSAGES.map((msg, i) => {
              const status = i < loadingStep ? 'completed' : i === loadingStep ? 'active' : 'pending';
              return (
                <div key={i} className={`loading-step loading-step--${status}`}>
                  <div className="loading-step__icon">
                    {status === 'completed' ? (
                      <svg className="loading-step__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : status === 'active' ? (
                      <div className="loading-step__spinner" />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    )}
                  </div>
                  <span className="loading-step__text">{msg}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Render: results ──────────────────────────────────────────────────────────

  if (phase === 'results') {
    const score = getTotalScore(answers);
    const profile = getProfile(score);
    const isFreeClear = answers.q4 === 'FREE_CLEAR';
    const isPFAware = answers.q7 === 'PF_AWARE';
    const isSTR = answers.q2 === 'STR';
    const isProfitMurky = answers.q11 === 'PROFIT_MURKY' || answers.q11 === 'PROFIT_UNKNOWN';

    // Build modifier note for the profile
    let modifierNote: string | null = null;
    if (profile.scoreMin === 12 && isFreeClear) {
      modifierNote = "Since you own free and clear, we'll focus on profit optimization and surplus sweeping rather than mortgage payoff.";
    } else if (profile.scoreMin === 21 && isPFAware) {
      modifierNote = "You already know about Profit First. Sequence is how you actually run it - automatically.";
    } else if (profile.scoreMin === 21 && isSTR) {
      modifierNote = "For short-term hosts like you, we'll lead with seasonal reserve automation to smooth out your income swings.";
    } else if (profile.scoreMin === 32 && isProfitMurky) {
      modifierNote = "We'll also show you your real profit margin per property - you might be surprised what you find.";
    } else if (profile.scoreMin === 41 && answers.q11 === 'PROFIT_UNKNOWN') {
      modifierNote = "We'll start by showing you whether your properties are actually making money after all costs.";
    }

    track(EVENTS.RENTAL_RESULT_SHOWN, { score, profile: profile.name, email: capturedEmail });

    return (
      <div className="rental-results">
        <div className="rental-results__container">
          <div className="rental-results__score-section">
            <div className="rental-results__score-badge">
              <span className="rental-results__score-number">{score}</span>
              <span className="rental-results__score-label">/ 48</span>
            </div>
            <p className="rental-results__score-subtext">Your Rental Money Health Score</p>
          </div>

          <div className={`rental-results__profile rental-results__profile--${profile.color}`}>
            <div className="rental-results__profile-header">
              <span className="rental-results__profile-badge">{profile.badge}</span>
              <span className="rental-results__profile-name">{profile.name}</span>
            </div>

            <h1 className="rental-results__headline">{profile.headline}</h1>

            <div className="rental-results__body">
              {profile.body.map((para, i) => (
                <p key={i} className="rental-results__para">{para}</p>
              ))}
              {modifierNote && (
                <p className="rental-results__modifier">{modifierNote}</p>
              )}
            </div>

            <p className="rental-results__oneliner">{profile.oneliner}</p>

            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => {
                track(EVENTS.SEQUENCE_SIGNUP_CLICKED, { profile: profile.name, score, flow: 'rental' });
                window.location.href = buildRentalSignupUrl({ q1: answers.q2, q3: answers.q3, email: capturedEmail, utms: utmRef.current });
              }}
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              }
              iconPosition="right"
            >
              {profile.cta}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: quiz or interstitial ─────────────────────────────────────────────

  const { question, helper, options } = getQuestionContent(currentQ, answers);
  const narratorText = pendingInterstitial?.narrator ?? null;
  const sp = pendingInterstitial?.spId ? SOCIAL_PROOF[pendingInterstitial.spId] : null;

  return (
    <>
      <Helmet>
        <title>Is Your Rental Actually Profitable? | Sequence</title>
        <meta name="description" content="Takes 3 minutes. Find out exactly where your rental profits are going - and how to stop the leaks." />
      </Helmet>
      <div className="rental-layout">
        {/* Sticky progress bar - percentage only per spec */}
        <div className="rental-layout__progress">
          <div className="rental-progress">
            <div className="rental-progress__bar">
              <div className="rental-progress__fill" style={{ width: `${progressPct}%` }} />
            </div>
            <div className="rental-progress__meta">
              <span className="rental-progress__pct">{progressPct}%</span>
              {milestoneMessage && (
                <span className="rental-progress__milestone">{milestoneMessage}</span>
              )}
            </div>
          </div>
          {(phase === 'interstitial' || currentQ > 1) && (
            <button className="rental-back-btn" onClick={handleBack} aria-label="Go back">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              Back
            </button>
          )}
        </div>

        <main className="rental-layout__content">
          <div className="rental-layout__container">
            {phase === 'interstitial' ? (
              /* Narrator / social proof interstitial */
              <div className="rental-interstitial">
                {narratorText && (
                  <p className="rental-interstitial__narrator">{narratorText}</p>
                )}
                {sp && (
                  <>
                    <blockquote className="rental-interstitial__quote">
                      "{sp.quote}"
                    </blockquote>
                    <div className="rental-interstitial__author">
                      <span className="rental-interstitial__name">{sp.author}</span>
                      <span className="rental-interstitial__role">{sp.role}</span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              /* Quiz question */
              <div className="quiz-step">
                <div className="quiz-step__header">
                  <h2 className="quiz-step__question">{question}</h2>
                  {helper && <p className="quiz-step__helper">{helper}</p>}
                </div>
                <div className="quiz-step__body">
                  <SingleSelectCards
                    options={options}
                    value={currentSelection}
                    onChange={setCurrentSelection}
                  />
                </div>
                {currentQ === 1 && (
                  <p className="rental-quiz__trust-line">Takes 3 minutes - No credit card - Used by 1,000+ landlords</p>
                )}
              </div>
            )}
          </div>
        </main>

        <div className="rental-layout__navigation">
          <div className="rental-layout__nav-container">
            {phase === 'interstitial' ? (
              <StepNavigation
                onBack={handleBack}
                onContinue={handleInterstitialContinue}
                canContinue
                continueLabel="Continue"
                showBack={false}
              />
            ) : (
              <StepNavigation
                onBack={handleBack}
                onContinue={handleContinue}
                canContinue={currentSelection !== null}
                continueLabel={currentQ === 13 ? 'Show My Score' : 'Continue'}
                showBack={false}
              />
            )}
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={(email) => {
          setCapturedEmail(email);
          setShowAuthModal(false);
          setPhase('loading');
        }}
        title="Your rental score is ready"
        subtitle="Enter your email to see your money health score and find out exactly where your rental profits are going."
        ctaLabel="Show My Score"
      />
    </>
  );
}
